import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
  Typography,
  TableSortLabel,
  Checkbox,
  Box,
  Button,
  MenuItem,
  Menu,
  Modal,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_URI } from "../../constants/GlobalConstants";
import fileDownload from "js-file-download";

const OrderList = ({
  p = { orders: [] },
  order = "asc",
  orderBy = "dealerName",
  onRequestSort = () => {},
  setRefresh = () => {},
  formatDate = () => {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [columnOrder, setColumnOrder] = useState([]);
  const [defaultColumnOrder, setDefaultColumnOrder] = useState([]);
  const menuRef = useRef(null);

  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedOrders(p.orders.map((order) => order._id));
      setSelectAll(true);
    } else {
      setSelectedOrders([]);
      setSelectAll(false);
    }
  };

  const handleClick = (id) => {
    setSelectedOrders((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id]
    );
  };

  const handleOpenModal = () => {
    if (!selectedOrders.length) {
      console.error("No order selected");
      return;
    }
    const selectedOrder = p.orders.find(
      (o) => o._id.toString() === selectedOrders[0].toString()
    );
    if (!selectedOrder) {
      console.error("Selected order not found in orders list");
      return;
    }
    if (selectedOrder && selectedOrder.productType !== "Materials") {
      const allKeys = [
        ...new Set(selectedOrder.orderArray.flatMap(Object.keys)),
      ];
      const defaultColumns = allKeys.filter(
        (key) =>
          key !== "id" &&
          key !== "customCost" &&
          selectedOrder.orderArray.some((item) => item[key] !== undefined)
      );
      const prioritizedColumns = defaultColumns.filter((key) =>
        selectedOrder.orderArray.some(
          (item) => item[key] && item[key].toString().trim() !== ""
        )
      );
      const essentialFields = ["width", "height", "liftStyle"];
      const finalColumns = [
        ...essentialFields.filter((field) => defaultColumns.includes(field)),
        ...prioritizedColumns.filter(
          (field) => !essentialFields.includes(field)
        ),
      ];
      if (finalColumns.length === 0) {
        console.warn("No valid columns found for reordering");
      }
      setColumnOrder(finalColumns);
      setDefaultColumnOrder(finalColumns);
      setOpenModal(true);
    } else {
      exportOrderPdf();
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleResetColumns = () => {
    setColumnOrder([...defaultColumnOrder]);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reorderedColumns = Array.from(columnOrder);
    const [movedColumn] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, movedColumn);
    setColumnOrder(reorderedColumns);
  };

  const exportCustomOrderPdf = async () => {
    try {
      const orderId = selectedOrders[0];
      const { data } = await axios.post(
        `${API_URI}/api/order/print-production-sheet/${orderId}`,
        { customColumnOrder: columnOrder },
        {
          headers: {
            Authorization: `Bearer ${userInfo?.token}`,
          },
          responseType: "blob",
        }
      );
      fileDownload(data, `OrderSheet.pdf`);
      handleCloseModal();
    } catch (error) {
      console.error("Error exporting custom order PDF:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        handleMenuClose();
        handleExportMenuClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const headCells = [
    { id: "select", numeric: false, disablePadding: true, label: "Select" },
    {
      id: "dealerName",
      numeric: false,
      disablePadding: false,
      label: "Dealer",
    },
    {
      id: "productType",
      numeric: false,
      disablePadding: false,
      label: "Product",
    },
    {
      id: "productTag",
      numeric: false,
      disablePadding: false,
      label: "ProductTag",
    },
    {
      id: "Date",
      numeric: false,
      disablePadding: false,
      label: "date",
    },
    {
      id: "productMaterial",
      numeric: false,
      disablePadding: false,
      label: "Material",
    },
    {
      id: "orderArrayLength",
      numeric: true,
      disablePadding: false,
      label: "# products",
    },
    {
      id: "orderStatus",
      numeric: false,
      disablePadding: false,
      label: "Status",
    },
  ];

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const sortedOrders = [...(p.orders || [])].sort((a, b) => {
    if (a[orderBy] < b[orderBy]) {
      return order === "asc" ? -1 : 1;
    }
    if (a[orderBy] > b[orderBy]) {
      return order === "asc" ? 1 : -1;
    }
    return 0;
  });

  const updateOrderStatus = async (status) => {
    try {
      const response = await axios.put(
        `${API_URI}/api/order/update-status`,
        { ids: selectedOrders, status },
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );
      setRefresh(true);
      setSelectedOrders([]);
      setSelectAll(false);
      handleMenuClose();
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  const exportOrderSheet = async () => {
    try {
      const orderId = selectedOrders[0];
      const { data } = await axios.get(
        `${API_URI}/api/order/export-excel-order-label/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
          responseType: "blob",
        }
      );
      fileDownload(data, `OrderLabel.xlsx`);
    } catch (error) {
      alert("😭 Oop! Internal Error, contact the Admin🤓 Error:", error);
    }
  };

  const exportOrderPdf = async () => {
    try {
      const orderId = selectedOrders[0];
      const { data } = await axios.get(
        `${API_URI}/api/order/print-production-sheet/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${userInfo?.token}`,
          },
          responseType: "blob",
        }
      );
      fileDownload(data, `OrderSheet.pdf`);
    } catch (error) {
      console.error("Error exporting order PDF:", error);
    }
  };

  return (
    <Box>
      {selectedOrders.length > 0 && (
        <Box
          mb={3}
          mt={2}
          px={2}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
        >
          {(userInfo?.roles?.includes("admin") ||
            userInfo?.roles?.includes("production_manager") ||
            userInfo?.isAdmin) && (
              <Button
              variant="contained"
              onClick={handleMenuOpen}
              endIcon={<MoreVertIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                py: 1,
                background: '#1976d2',
                color: 'white',
                borderRadius: 1,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  background: '#1565c0',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              Change Status
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleExportMenuOpen}
            endIcon={<MoreVertIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 1,
              background: '#9c27b0',
              color: 'white',
              borderRadius: 1,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': {
                background: '#7b1fa2',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            Export
          </Button>
          <Menu
            id="export-menu"
            anchorEl={exportMenuAnchorEl}
            keepMounted
            open={Boolean(exportMenuAnchorEl)}
            onClose={handleExportMenuClose}
          >
            <MenuItem onClick={handleOpenModal}>
              Customize and Print Production Sheet
            </MenuItem>
            <MenuItem onClick={exportOrderPdf}>Print Production Sheet</MenuItem>
            <MenuItem onClick={exportOrderSheet}>Export Order Label</MenuItem>
          </Menu>
          <Menu
            id="status-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            ref={menuRef}
          >
            <MenuItem onClick={() => updateOrderStatus("ready_to_process")}>
              Ready to Process
            </MenuItem>
            <MenuItem onClick={() => updateOrderStatus("processing")}>
              Processing
            </MenuItem>
            <MenuItem onClick={() => updateOrderStatus("processed")}>
              Processed
            </MenuItem>
            <MenuItem onClick={() => updateOrderStatus("delivered")}>
              Delivered
            </MenuItem>
          </Menu>
        </Box>
      )}

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper", // Ensure background is white
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "80vh",
            overflowY: "auto",
            color: "text.primary", // Ensure default text color is visible
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6" color="text.primary">
              Customize Column Order
            </Typography>
            <Tooltip title="Reset to default order">
              <IconButton onClick={handleResetColumns} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Drag and drop the columns to reorder them for the PDF.
          </Typography>
          {columnOrder.length > 0 ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="columns" direction="vertical">
                {(provided) => (
                  <List
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{ bgcolor: "grey.50", borderRadius: 1, p: 1 }}
                  >
                    {columnOrder.map((column, index) => (
                      <Draggable
                        key={column}
                        draggableId={column}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              bgcolor: snapshot.isDragging
                                ? "primary.light"
                                : "background.paper",
                              border: "1px solid",
                              borderColor: snapshot.isDragging
                                ? "primary.main"
                                : "grey.200",
                              borderRadius: 1,
                              mb: 1,
                              "&:hover": {
                                bgcolor: "grey.100",
                                transition: "background-color 0.2s",
                              },
                            }}
                            role="listitem"
                            aria-label={`Drag to reorder column ${column}`}
                          >
                            <ListItemIcon>
                              <DragIndicatorIcon sx={{ color: "grey.500" }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={column}
                              primaryTypographyProps={{ color: "text.primary" }}
                            />
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <Typography color="text.primary">
              No columns available for reordering.
            </Typography>
          )}
          <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              color="secondary"
              sx={{
                color: "text.primary", // Ensure button text is visible
                borderColor: "grey.500",
                "&:hover": {
                  borderColor: "grey.700",
                  bgcolor: "grey.100",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={exportCustomOrderPdf}
              disabled={columnOrder.length === 0}
              sx={{
                color: "common.white", // Ensure button text is white on primary background
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              Generate PDF
            </Button>
          </Box>
        </Box>
      </Modal>

      <TableContainer component={Paper} className="mt-3">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={
                    selectedOrders.length > 0 &&
                    selectedOrders.length < (p.orders || []).length
                  }
                  checked={selectAll}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? "right" : "left"}
                  padding={headCell.disablePadding ? "none" : "normal"}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : "asc"}
                    onClick={createSortHandler(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedOrders.map((order) => (
              <TableRow
                key={order._id}
                hover
                role="checkbox"
                aria-checked={selectedOrders.includes(order._id)}
                tabIndex={-1}
                selected={selectedOrders.includes(order._id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={selectedOrders.includes(order._id)}
                    onChange={() => handleClick(order._id)}
                    inputProps={{ "aria-labelledby": `order-${order._id}` }}
                  />
                </TableCell>
                <TableCell padding="checkbox"> </TableCell>
                <TableCell>
                  <Link to={`/order/order-detail/${order._id}`}>
                    <Typography color="textPrimary" variant="body2">
                      {order.dealerName}
                    </Typography>
                  </Link>
                </TableCell>
                <TableCell>
                  <Link to={`/order/order-detail/${order._id}`}>
                    <Typography color="textPrimary" variant="body2">
                      {order.productType}
                    </Typography>
                  </Link>
                </TableCell>
                <TableCell>
                  <Link to={`/order/order-detail/${order._id}`}>
                    <Typography color="textPrimary" variant="body2">
                      {order.tag}
                    </Typography>
                  </Link>
                </TableCell>
                <TableCell>
                  <Link to={`/order/order-detail/${order._id}`}>
                    <Typography color="textPrimary" variant="body2">
                      {formatDate(order.createdAt)}
                    </Typography>
                  </Link>
                </TableCell>
                <TableCell>
                  <Link to={`/order/order-detail/${order._id}`}>
                    <Typography color="textPrimary" variant="body2">
                      {order?.productMaterial?.name}
                    </Typography>
                  </Link>
                </TableCell>
                <TableCell align="right">
                  <Link to={`/order/order-detail/${order._id}`}>
                    <Typography color="textPrimary" variant="body2">
                      {order.orderArray.length}
                    </Typography>
                  </Link>
                </TableCell>
                <TableCell>
                  <Badge
                    color={
                      order.orderStatus === "delivered"
                        ? "success"
                        : order.orderStatus === "processed"
                        ? "info"
                        : order.orderStatus === "processing"
                        ? "warning"
                        : "default"
                    }
                    variant="dot"
                  >
                    {order.orderStatus === "ready_to_process" &&
                      "Ready to Process"}
                    {order.orderStatus === "processing" && "Processing"}
                    {order.orderStatus === "processed" && "Processed"}
                    {order.orderStatus === "delivered" && "Delivered"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OrderList;
